import { createFileRoute, Link } from "@tanstack/react-router";
import { claimAudio, registerAudioOwner, type AudioOwner } from "@/lib/audio-bus";
import { withMemberGate } from "@/lib/with-member-gate";
import { useEffect, useRef, useState } from "react";
import { getAudioContext, resumeAudio, unlockAudio } from "@/lib/audio-context";
import {
  ArrowLeft, ArrowRight, ChevronLeft, Clock, ShieldAlert, Wind, HeartHandshake, Music, BookOpen, X,
  Plus, Mic, Play, Pause, Check, Quote, Heart, Headphones, PersonStanding, Leaf,
} from "lucide-react";
import {
  UNIVERSAL_TAPPING_SESSION,
  TAPPING_POINTS,
  ROUND_ORDER,
  type TappingPointId,
} from "@/lib/tapping-data";
import { FavoriteButton } from "@/components/FavoriteButton";
import { prefetchTts, unlockAudioElement, useTts, type TtsCaller } from "@/lib/elevenVoice";
import tappingFigureImg from "@/assets/tapping-figure.jpg";
import { useScrollTopOnChange } from "@/hooks/use-scroll-top-on-change";

export const Route = createFileRoute("/tapping")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "EFT Tapping — Gentle Guided Practice — Recovery Trauma™" },
      {
        name: "description",
        content:
          "A gentle, guided EFT tapping practice you can follow at your own pace — one tapping point at a time, with optional sound and step-by-step flashcards.",
      },
      { property: "og:title", content: "EFT Tapping — Recovery Trauma™" },
      {
        property: "og:description",
        content: "A gentle, guided tapping practice you can follow at your own pace.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/tapping" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/tapping" }],
  }),
  component: withMemberGate(TappingPage, "Tapping (EFT)"),
});

/**
 * Plain-language "what to do" guidance per tapping point, plus a percentage-based
 * coordinate (relative to the watercolor figure image, treated as a 100×100 grid)
 * so the marker overlay scales with the figure on any screen size.
 */
const POINT_GUIDE: Record<
  TappingPointId,
  { emoji: string; instruction: string; xPct: number; yPct: number }
> = {
  "karate-chop": {
    emoji: "🤚",
    instruction:
      "Tap gently on the fleshy side of your hand, just below your little finger, using two or three fingers.",
    xPct: 24,
    yPct: 78,
  },
  eyebrow: {
    emoji: "👁️",
    instruction: "Tap softly at the inner edge of one eyebrow, just above the bridge of the nose.",
    xPct: 47,
    yPct: 26,
  },
  "side-of-eye": {
    emoji: "👀",
    instruction: "Tap gently on the bone at the outside corner of one eye.",
    xPct: 42,
    yPct: 29,
  },
  "under-eye": {
    emoji: "👁️",
    instruction: "Tap softly on the bone just below one eye, in line with the pupil.",
    xPct: 47,
    yPct: 32,
  },
  "under-nose": {
    emoji: "👃",
    instruction: "Tap gently in the small dip between your nose and your upper lip.",
    xPct: 50,
    yPct: 35,
  },
  chin: {
    emoji: "💋",
    instruction: "Tap softly in the crease between your lower lip and your chin.",
    xPct: 50,
    yPct: 38,
  },
  collarbone: {
    emoji: "🤲",
    instruction:
      "Tap gently just below your collarbone, a little to one side of the dip in your throat.",
    xPct: 47,
    yPct: 47,
  },
  "under-arm": {
    emoji: "🫶",
    instruction: "Tap softly on your side, about a hand's width below your armpit.",
    xPct: 33,
    yPct: 58,
  },
  "top-of-head": {
    emoji: "🧠",
    instruction: "Tap softly with all your fingertips on the very crown of your head.",
    xPct: 50,
    yPct: 20,
  },
};

const FEELING_OPTIONS = [
  { id: "calmer", emoji: "🌿", label: "A little calmer" },
  { id: "same", emoji: "🤍", label: "About the same" },
  { id: "emotional", emoji: "💛", label: "More emotional" },
  { id: "unsure", emoji: "🪶", label: "Not sure" },
] as const;
type FeelingId = (typeof FEELING_OPTIONS)[number]["id"];

const NEXT_STEPS = [
  { to: "/breathing" as const, icon: Wind, label: "Breathing", blurb: "Gently guided breathing practices." },
  { to: "/grounding" as const, icon: HeartHandshake, label: "Grounding", blurb: "Sensory anchors for the present moment." },
  { to: "/sounds" as const, icon: Music, label: "Sounds & Meditations", blurb: "Calming nature and sleep sounds." },
  { to: "/journal" as const, icon: BookOpen, label: "Journal", blurb: "Put words to what you noticed." },
];

type Phase = "intro" | "practice" | "explore" | "finished";

const SOUND_OPTIONS = [
  { id: "none", label: "None" },
  { id: "ambient", label: "Soft ambient" },
] as const;
type SoundId = (typeof SOUND_OPTIONS)[number]["id"];

function Collapsible({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <details className="group rounded-2xl border border-gold/25 bg-card/70 px-4 py-1">
      <summary className="flex min-h-12 cursor-pointer list-none items-center gap-3 text-sm font-medium text-foreground [&::-webkit-details-marker]:hidden">
        <Icon className="h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
        <span className="flex-1">{title}</span>
        <Plus className="h-4 w-4 text-[var(--gold-deep)] transition-transform group-open:rotate-45" aria-hidden />
      </summary>
      <div className="space-y-2 pb-3 pt-1 text-sm leading-relaxed text-foreground/85">{children}</div>
    </details>
  );
}

function TappingPage() {
  const session = UNIVERSAL_TAPPING_SESSION;
  const [phase, setPhase] = useState<Phase>("intro");
  const [stepIdx, setStepIdx] = useState(0);
  const [selectedPoint, setSelectedPoint] = useState<TappingPointId | null>(null);
  const [feeling, setFeeling] = useState<FeelingId | null>(null);
  const [paused, setPaused] = useState(false);

  const [sound, setSound] = useState<SoundId>("none");
  const [voiceOn, setVoiceOn] = useState(true);
  const { caller: ttsCaller } = useTts();

  useScrollTopOnChange(phase);

  const ownerRef = useRef<AudioOwner>({
    stop: () => {
      stopAmbient();
      cancelSpeech();
    },
    // The guided-voice <audio> element belongs to this screen. Returning false
    // here made the audio bus treat our own voice as a rival player and stop
    // us (cancelSpeech → currentAudio = null) the moment playback started —
    // which is why the voice never sounded.
    ownsElement: (el) => el === currentAudio,
  });
  useEffect(() => registerAudioOwner(ownerRef.current), []);
  useEffect(() => () => {
    stopAmbient();
    cancelSpeech();
  }, []);

  // Background sound — never autoplays; only runs during an unpaused practice.
  useEffect(() => {
    if (phase !== "practice" || sound === "none") {
      stopAmbient();
      return;
    }
    claimAudio(ownerRef.current);
    startAmbient(paused ? 0 : 0.35);
    return () => stopAmbient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, sound]);

  useEffect(() => {
    setAmbientMasterVolume(paused ? 0 : 0.35);
  }, [paused]);

  const currentId = ROUND_ORDER[stepIdx];
  const currentPoint = TAPPING_POINTS.find((p) => p.id === currentId)!;
  const guide = POINT_GUIDE[currentId];
  const reminderFor = (idx: number) =>
    idx === 0
      ? session.setup
      : session.reminders[ROUND_ORDER[idx]] ?? "Notice what's here, without needing to change it.";
  const reminder = reminderFor(stepIdx);

  const speakStep = (idx: number, force = false) => {
    if (!voiceOn && !force) return;
    const id = ROUND_ORDER[idx];
    const label = TAPPING_POINTS.find((p) => p.id === id)?.label ?? "";
    claimAudio(ownerRef.current);
    speak(ttsCaller, `${label}. ${POINT_GUIDE[id].instruction} Repeat after me: ${reminderFor(idx)}`, VOICE_IDS.female);
  };

  const goTo = (idx: number) => {
    const next = Math.max(0, Math.min(ROUND_ORDER.length - 1, idx));
    setStepIdx(next);
    setPaused(false);
    cancelSpeech();
    speakStep(next);
  };

  const beginPractice = (startIdx = 0) => {
    void unlockAudio(); // inside the tap: arms iPhone playback even on silent
    setFeeling(null);
    setStepIdx(startIdx);
    setPaused(false);
    setPhase("practice");
    cancelSpeech();
    speakStep(startIdx);
  };

  const togglePause = () => {
    if (paused) {
      setPaused(false);
      if (voiceOn) {
        if (currentAudio && currentAudio.src) void currentAudio.play().catch(() => speakStep(stepIdx));
        else speakStep(stepIdx);
      }
    } else {
      setPaused(true);
      try { currentAudio?.pause(); } catch {}
    }
  };

  const toggleVoice = () => {
    const next = !voiceOn;
    setVoiceOn(next);
    if (!next) cancelSpeech();
    else if (phase === "practice" && !paused) speakStep(stepIdx, true);
  };

  const endPractice = () => {
    cancelSpeech();
    stopAmbient();
    setPhase("intro");
  };

  const finish = () => {
    cancelSpeech();
    stopAmbient();
    setPhase("finished");
  };

  const voiceToggle = (
    <div className="flex min-h-11 items-center justify-between gap-3">
      <span className="flex items-center gap-2 text-sm font-medium text-foreground">
        <Mic className="h-4 w-4 text-[var(--gold-deep)]" aria-hidden /> Guided voice
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={voiceOn}
        aria-label="Guided voice"
        onClick={toggleVoice}
        className={`relative inline-flex h-8 w-16 shrink-0 items-center rounded-full border transition ${
          voiceOn ? "border-[var(--gold-deep)] bg-[var(--gold-deep)]" : "border-border bg-muted"
        }`}
      >
        <span
          className={`absolute text-[10px] font-bold ${voiceOn ? "start-2 text-primary-foreground" : "end-2 text-muted-foreground"}`}
        >
          {voiceOn ? "ON" : "OFF"}
        </span>
        <span
          className={`absolute h-6 w-6 rounded-full bg-card shadow transition-all ${voiceOn ? "end-1" : "start-1"}`}
        />
      </button>
    </div>
  );

  const soundPicker = (
    <div className="flex min-h-11 flex-wrap items-center justify-between gap-2">
      <span className="flex items-center gap-2 text-sm font-medium text-foreground">
        <Music className="h-4 w-4 text-[var(--gold-deep)]" aria-hidden /> Background sound
      </span>
      <div role="radiogroup" aria-label="Background sound" className="inline-flex rounded-full border border-gold/30 bg-cream/50 p-0.5">
        {SOUND_OPTIONS.map((o) => (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={sound === o.id}
            onClick={() => setSound(o.id)}
            className={`min-h-9 rounded-full px-3 text-xs font-medium transition ${
              sound === o.id ? "bg-[var(--gold-deep)] text-primary-foreground shadow" : "text-foreground/75"
            }`}
          >
            {sound === o.id && "✓ "}
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );

  const audioPanel = (
    <div className="space-y-1 rounded-2xl border border-gold/25 bg-card/70 px-4 py-2">
      {voiceToggle}
      {soundPicker}
    </div>
  );

  /* ── Practice (focused) ─────────────────────────────────── */
  if (phase === "practice") {
    const isLast = stepIdx === ROUND_ORDER.length - 1;
    return (
      <main className="mx-auto max-w-md px-4 pt-4 pb-[calc(7rem+env(safe-area-inset-bottom))]">
        <div className="flex items-center justify-between">
          <button type="button" onClick={endPractice} aria-label="Back" className="inline-flex h-10 w-10 items-center justify-center rounded-full text-foreground">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <p className="font-medium text-foreground">Guided EFT Tapping</p>
          <button type="button" onClick={endPractice} aria-label="End practice" className="inline-flex h-10 w-10 items-center justify-center rounded-full text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-cream">
          <div className="h-full rounded-full bg-[var(--gold-deep)] transition-all" style={{ width: `${((stepIdx + 1) / ROUND_ORDER.length) * 100}%` }} />
        </div>

        <div className="mt-3 text-center" aria-live="polite">
          <p className="text-xs text-muted-foreground">Step {stepIdx + 1} of {ROUND_ORDER.length}</p>
          <h1 className="mt-1 font-serif text-2xl text-foreground">
            <span className="me-2" aria-hidden>{guide.emoji}</span>
            {currentPoint.label}
          </h1>
          <p className="mx-auto mt-1 max-w-sm text-sm text-foreground/85">{guide.instruction}</p>
        </div>

        <div className="relative mx-auto mt-3 w-full max-w-[300px]">
          <TappingFigure highlight={currentId} />
        </div>

        <div className="mt-3 flex items-center justify-center gap-5">
          <button type="button" onClick={() => goTo(stepIdx - 1)} disabled={stepIdx === 0} aria-label="Previous step"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 bg-card text-foreground disabled:opacity-40">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <button type="button" onClick={togglePause} aria-label={paused ? "Play" : "Pause"}
            className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[var(--gold-deep)] text-primary-foreground shadow-[var(--shadow-gold)]">
            {paused ? <Play className="h-6 w-6" /> : <Pause className="h-6 w-6" />}
          </button>
          {isLast ? (
            <button type="button" onClick={finish} aria-label="Finish practice"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 bg-card text-foreground">
              <Check className="h-5 w-5" />
            </button>
          ) : (
            <button type="button" onClick={() => goTo(stepIdx + 1)} aria-label="Next step"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 bg-card text-foreground">
              <ArrowRight className="h-5 w-5" />
            </button>
          )}
        </div>
        {paused && <p className="mt-2 text-center text-xs text-muted-foreground">Paused — press play when you're ready.</p>}

        <div className="mt-4 rounded-2xl border border-gold/25 bg-cream/40 px-4 py-3">
          <p className="flex items-center gap-1.5 text-xs font-medium text-[var(--gold-deep)]">
            <Quote className="h-3.5 w-3.5" aria-hidden /> Repeat after me:
          </p>
          <p className="mt-1 text-sm text-foreground/90">“{reminder}”</p>
          <p className="mt-1 text-[11px] text-muted-foreground">Notice what you're feeling without needing to change it.</p>
        </div>

        <div className="mt-3">{audioPanel}</div>

        <button type="button" onClick={isLast ? finish : endPractice}
          className="mt-3 inline-flex min-h-11 w-full items-center justify-center rounded-full border border-gold/50 bg-card text-sm font-medium text-[var(--gold-deep)]">
          {isLast ? "Finish practice" : "End practice"}
        </button>
      </main>
    );
  }

  /* ── Explore tapping points ─────────────────────────────── */
  if (phase === "explore") {
    const sel = selectedPoint;
    const selLabel = sel ? TAPPING_POINTS.find((p) => p.id === sel)?.label : null;
    return (
      <main className="mx-auto max-w-md px-4 pt-4 pb-[calc(7rem+env(safe-area-inset-bottom))]">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setPhase("intro")} aria-label="Back" className="inline-flex h-10 w-10 items-center justify-center rounded-full text-foreground">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h1 className="font-medium text-foreground">Explore the tapping points</h1>
        </div>
        <p className="mt-1 text-center text-sm text-muted-foreground">Tap any point on the figure to see where it is and how to tap it.</p>
        <div className="mx-auto mt-3 w-full max-w-[320px]">
          <TappingFigure highlight={sel ?? "karate-chop"} interactive onSelect={setSelectedPoint} dimUnselected={sel !== null} />
        </div>
        {sel && (
          <div className="relative mt-4 rounded-2xl border border-gold/30 bg-card p-4 shadow-[var(--shadow-gold)]" aria-live="polite">
            <button type="button" onClick={() => setSelectedPoint(null)} aria-label="Close point details" className="absolute end-2 top-2 inline-flex h-9 w-9 items-center justify-center rounded-full text-foreground">
              <X className="h-4 w-4" />
            </button>
            <p className="font-serif text-lg text-foreground"><span className="me-2" aria-hidden>{POINT_GUIDE[sel].emoji}</span>{selLabel}</p>
            <p className="mt-2 text-sm font-medium text-foreground">How to tap</p>
            <ul className="mt-1 list-disc space-y-1 ps-5 text-sm text-foreground/85">
              <li>{POINT_GUIDE[sel].instruction}</li>
              <li>Use the fingertips of your other hand.</li>
              <li>Tap gently but firmly, about 5–7 times.</li>
            </ul>
            <button type="button" onClick={() => beginPractice(ROUND_ORDER.indexOf(sel))} className="btn-gold mt-3 inline-flex w-full items-center justify-center gap-2 text-sm">
              Practice this point <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
        {!sel && (
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {ROUND_ORDER.map((id) => (
              <button key={id} type="button" onClick={() => setSelectedPoint(id)}
                className="min-h-9 rounded-full border border-gold/30 bg-card px-3 text-xs font-medium text-foreground">
                {TAPPING_POINTS.find((p) => p.id === id)?.label}
              </button>
            ))}
          </div>
        )}
      </main>
    );
  }

  /* ── Finished ───────────────────────────────────────────── */
  if (phase === "finished") {
    return (
      <main className="mx-auto max-w-2xl px-4 py-8 pb-[calc(7rem+env(safe-area-inset-bottom))]">
        <h1 className="text-center font-serif text-3xl text-foreground">How do you feel now?</h1>
        <p className="mx-auto mt-3 max-w-md text-center text-sm text-muted-foreground">
          You don't need to feel completely different for the practice to have been worthwhile.
        </p>
        <div className="mx-auto mt-5 grid max-w-md grid-cols-2 gap-2">
          {FEELING_OPTIONS.map((f) => (
            <button key={f.id} type="button" onClick={() => setFeeling(f.id)} aria-pressed={feeling === f.id}
              className={`min-h-12 rounded-2xl border px-3 py-2 text-sm font-medium transition ${
                feeling === f.id ? "border-gold/60 bg-[var(--gradient-gold)] text-foreground" : "border-border bg-card text-foreground/85 hover:border-gold/40"
              }`}>
              <span className="me-1.5" aria-hidden>{f.emoji}</span>
              {f.label}
            </button>
          ))}
        </div>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button type="button" onClick={() => beginPractice(0)} className="btn-gold text-sm">Repeat practice</button>
          <Link to="/eft-flashcards" className="inline-flex min-h-11 items-center rounded-full border border-gold/40 bg-cream/60 px-4 text-sm font-medium text-foreground hover:bg-gold/10">
            EFT Flashcards
          </Link>
          <button type="button" onClick={() => setPhase("intro")} className="inline-flex min-h-11 items-center rounded-full border border-border bg-card px-4 text-sm font-medium text-foreground">
            Back to EFT Tapping
          </button>
        </div>
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {NEXT_STEPS.map((s) => (
            <Link key={s.to} to={s.to} className="rounded-2xl border border-gold/20 bg-card/70 p-4 hover:border-gold/40">
              <s.icon className="h-5 w-5 text-[var(--gold-deep)]" aria-hidden />
              <p className="mt-2 font-medium text-foreground">{s.label}</p>
              <p className="mt-1 text-xs text-muted-foreground">{s.blurb}</p>
            </Link>
          ))}
        </div>
      </main>
    );
  }

  /* ── Intro (simple & clear) ─────────────────────────────── */
  const facts = [
    { icon: Clock, label: "5–10 minutes" },
    { icon: Heart, label: "Pause anytime" },
    { icon: Headphones, label: "Voice & sound optional" },
  ];
  return (
    <main className="mx-auto max-w-md px-4 pt-5 pb-[calc(7rem+env(safe-area-inset-bottom))]">
      <div className="flex items-center justify-end">
        <FavoriteButton storageKey="rt:fav-tapping" id={session.slug} label={session.title} sub="EFT tapping practice" to="/tapping" size="sm" />
      </div>
      <h1 className="mt-3 font-serif text-4xl text-foreground">EFT Tapping</h1>
      <p className="mt-2 text-muted-foreground">A gentle guided tapping practice you can follow at your own pace.</p>

      <ul className="mt-4 grid grid-cols-3 gap-2 text-center">
        {facts.map((f) => (
          <li key={f.label} className="flex flex-col items-center gap-1 text-xs text-foreground/85">
            <f.icon className="h-6 w-6 text-[var(--gold-deep)]" aria-hidden />
            {f.label}
          </li>
        ))}
      </ul>

      <button type="button" onClick={() => beginPractice(0)} className="btn-gold mt-5 inline-flex w-full items-center justify-center gap-2 text-base">
        Start Guided Tapping <ArrowRight className="h-4 w-4" />
      </button>
      <div className="mt-3 space-y-2">
        <button type="button" onClick={() => setPhase("explore")}
          className="flex min-h-12 w-full items-center gap-3 rounded-full border border-gold/40 bg-card px-5 text-sm font-medium text-foreground">
          <PersonStanding className="h-5 w-5 text-[var(--gold-deep)]" aria-hidden />
          <span className="flex-1 text-start">Explore Tapping Points</span>
          <ArrowRight className="h-4 w-4" />
        </button>
        <Link to="/eft-flashcards"
          className="flex min-h-12 w-full items-center gap-3 rounded-full border border-gold/40 bg-card px-5 text-sm font-medium text-foreground">
          <BookOpen className="h-5 w-5 text-[var(--gold-deep)]" aria-hidden />
          <span className="flex-1 text-start">EFT Flashcards</span>
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="mt-4">{audioPanel}</div>
      <p className="mt-1 px-1 text-[11px] text-muted-foreground">Nothing plays until you press Start.</p>

      <div className="mt-4 space-y-2">
        <Collapsible icon={Leaf} title="What is EFT Tapping?">
          <p>EFT involves gently tapping on a sequence of points on the face and upper body while bringing attention to thoughts, feelings or sensations.</p>
          <p>Some people use tapping as a calming or reflective practice during moments of stress, emotional overwhelm or when they want to reconnect with the present. You do not need to force yourself to feel differently — simply notice what comes up and stop whenever you want.</p>
          <p><span className="font-medium">What we'll do:</span> You'll be guided through a short sequence of tapping points. Follow the prompts, repeat the words silently or aloud, or simply focus on the tapping sensation.</p>
          <p><span className="font-medium">You are in control:</span> You can pause, skip a tapping point or stop completely at any time.</p>
        </Collapsible>
        <Collapsible icon={ShieldAlert} title="Before you begin">
          <p>EFT tapping on Recovery Trauma™ is provided as a self-guided wellbeing and reflection tool. It is not therapy, medical treatment or a substitute for professional mental-health or medical care.</p>
          <p>People can respond differently to tapping and emotional reflection. If the practice feels uncomfortable, increases distress or brings up material you do not want to explore, stop the exercise and return to something that feels safer or more supportive.</p>
          <p>You can skip any prompt, keep your eyes open and stop at any time.</p>
        </Collapsible>
        <Collapsible icon={Heart} title="Other supportive tools">
          <div className="grid gap-2">
            {NEXT_STEPS.map((s) => (
              <Link key={s.to} to={s.to} className="flex items-center gap-3 rounded-xl border border-gold/20 bg-cream/40 p-3">
                <s.icon className="h-4 w-4 shrink-0 text-[var(--gold-deep)]" aria-hidden />
                <span className="min-w-0">
                  <span className="block font-medium text-foreground">{s.label}</span>
                  <span className="block text-xs text-muted-foreground">{s.blurb}</span>
                </span>
              </Link>
            ))}
          </div>
        </Collapsible>
      </div>
    </main>
  );
}

/* ───────────────────── Optional background sound ───────────────────── */

let ambientCtx: AudioContext | null = null;
let ambientMaster: GainNode | null = null;
let ambientNodes: Array<AudioNode & { stop?: (t?: number) => void }> = [];

function startAmbient(volume: number) {
  if (typeof window === "undefined") return;
  stopAmbient();
  const ctx = getAudioContext();
  if (!ctx) return;
  resumeAudio();
  ambientCtx = ctx;
  const master = ctx.createGain();
  master.gain.value = Math.max(0, Math.min(1, volume));
  master.connect(ctx.destination);
  ambientMaster = master;

  // Pink noise buffer (Paul Kellet approximation)
  const size = 2 * ctx.sampleRate;
  const buffer = ctx.createBuffer(1, size, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
  for (let i = 0; i < size; i++) {
    const w = Math.random() * 2 - 1;
    b0 = 0.99886 * b0 + w * 0.0555179;
    b1 = 0.99332 * b1 + w * 0.0750759;
    b2 = 0.969 * b2 + w * 0.153852;
    b3 = 0.8665 * b3 + w * 0.3104856;
    b4 = 0.55 * b4 + w * 0.5329522;
    b5 = -0.7616 * b5 - w * 0.016898;
    data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
    b6 = w * 0.115926;
  }
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.loop = true;
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 900;
  const noiseGain = ctx.createGain();
  noiseGain.gain.value = 0.5;
  src.connect(lp).connect(noiseGain).connect(master);
  src.start();
  ambientNodes.push(src);

  const padGain = ctx.createGain();
  padGain.gain.value = 0.08;
  padGain.connect(master);
  [174.6, 220, 261.6].forEach((freq, i) => {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.value = 0;
    osc.connect(g).connect(padGain);
    osc.start();
    g.gain.setTargetAtTime(0.5 / (i + 1), ctx.currentTime, 3);
    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = 0.04 + i * 0.03;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.22 / (i + 1);
    lfo.connect(lfoGain).connect(g.gain);
    lfo.start();
    ambientNodes.push(osc, lfo);
  });
}

function setAmbientMasterVolume(v: number) {
  if (!ambientCtx || !ambientMaster) return;
  const clamped = Math.max(0, Math.min(1, v));
  ambientMaster.gain.setTargetAtTime(clamped, ambientCtx.currentTime, 0.15);
}

function stopAmbient() {
  for (const n of ambientNodes) {
    try { n.stop?.(); } catch {}
    try { n.disconnect(); } catch {}
  }
  ambientNodes = [];
  try { ambientMaster?.disconnect(); } catch {}
  ambientCtx = null;
  ambientMaster = null;
}

/* ───────────────────── Optional spoken guidance ───────────────────── */

const VOICE_IDS: Record<string, string> = {
  female: "2qfp6zPuviqeCOZIE9RZ",
};
const ttsCache = new Map<string, string>();
const ttsInflight = new Map<string, Promise<string>>();
let currentAudio: HTMLAudioElement | null = null;

async function fetchTtsUrl(tts: TtsCaller, text: string, voiceId: string): Promise<string> {
  const key = `${voiceId}::${text}`;
  const cached = ttsCache.get(key);
  if (cached) return cached;
  const pending = ttsInflight.get(key);
  if (pending) return pending;
  const p = prefetchTts(tts, text, voiceId).then((url) => {
    ttsCache.set(key, url);
    ttsInflight.delete(key);
    return url;
  });
  ttsInflight.set(key, p);
  return p;
}

function speak(tts: TtsCaller, text: string, voiceId: string) {
  if (typeof window === "undefined") return;
  const audio = currentAudio ?? new Audio();
  currentAudio = audio;
  unlockAudioElement(audio, 0.95);
  fetchTtsUrl(tts, text, voiceId)
    .then((url) => {
      if (currentAudio !== audio) return;
      audio.src = url;
      audio.volume = 0.95;
      void audio.play().catch(() => {});
    })
    .catch((err) => console.error("TTS error", err));
}

function cancelSpeech() {
  if (currentAudio) {
    try { currentAudio.pause(); } catch {}
    currentAudio = null;
  }
  if (typeof window !== "undefined" && "speechSynthesis" in window) {
    window.speechSynthesis.cancel();
  }
}

/* ───────────────────── Figure ───────────────────── */

function TappingFigure({
  highlight,
  interactive = false,
  onSelect,
  dimUnselected = false,
}: {
  highlight: TappingPointId;
  interactive?: boolean;
  onSelect?: (id: TappingPointId) => void;
  dimUnselected?: boolean;
}) {
  const active = POINT_GUIDE[highlight];

  return (
    <figure
      className="relative aspect-square w-full overflow-hidden rounded-3xl border border-gold/30 bg-cream/40 shadow-[var(--shadow-gold)]"
      aria-label={`Tapping point illustration — currently highlighting ${highlight.replace(/-/g, " ")}`}
    >
      <img
        src={tappingFigureImg}
        alt="Calming watercolor figure showing tapping points"
        width={1024}
        height={1024}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover"
      />

      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-cream/30"
      />

      {(Object.entries(POINT_GUIDE) as [TappingPointId, (typeof POINT_GUIDE)[TappingPointId]][]).map(
        ([id, p]) => {
          const isActive = id === highlight;
          const dim = dimUnselected && !isActive;
          const dot = (
            <>
              {isActive && (
                <span
                  aria-hidden
                  className="absolute start-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--gold-deep)] opacity-30"
                  style={{ animation: "rt-tap-pulse 1.4s ease-in-out infinite" }}
                />
              )}
              <span
                className="block rounded-full border shadow-sm"
                style={{
                  width: isActive ? 18 : 14,
                  height: isActive ? 18 : 14,
                  borderColor: "rgba(0,0,0,0.35)",
                  background: isActive ? "var(--gold-deep)" : "var(--gold)",
                  opacity: dim ? 0.55 : 1,
                }}
              />
            </>
          );
          const style: React.CSSProperties = {
            left: `${p.xPct}%`,
            top: `${p.yPct}%`,
            transform: "translate(-50%, -50%)",
          };
          if (interactive && onSelect) {
            const label = TAPPING_POINTS.find((x) => x.id === id)?.label ?? id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => onSelect(id)}
                aria-label={`Show guidance for ${label}`}
                aria-pressed={isActive}
                className="absolute inline-flex h-11 w-11 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-deep)]"
                style={style}
              >
                <span className="relative inline-flex items-center justify-center">{dot}</span>
              </button>
            );
          }
          return (
            <span key={id} className="pointer-events-none absolute" style={style}>
              {dot}
            </span>
          );
        },
      )}

      <figcaption className="absolute bottom-2 start-1/2 -translate-x-1/2 rounded-full border border-gold/40 bg-card/90 px-3 py-1 text-[11px] font-medium text-foreground backdrop-blur-sm">
        {active.emoji} {highlight.replace(/-/g, " ")}
      </figcaption>

      <style>{`
        @keyframes rt-tap-pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.35; }
          50% { transform: translate(-50%, -50%) scale(1.4); opacity: 0.1; }
        }
      `}</style>
    </figure>
  );
}
